/**
 * Create a map pin marker element (button) with an SVG teardrop shape.
 * The icon is applied via CSS background-image on the .marker-pin-icon element.
 *
 * @param {string} className - CSS class for the marker type (e.g. 'marker-layer-dining')
 * @param {string} color - Fill color for the pin (e.g. '#00759b')
 * @param {string} label - Accessible label for the button
 * @returns {HTMLButtonElement}
 */
export default function createMarkerPin(className, color, label) {
	const el = document.createElement('button');
	el.className = 'marker-layer ' + className;
	el.setAttribute('aria-label', label);

	el.innerHTML =
		`<svg class="marker-pin-shape" viewBox="-1.5 -1.5 33 44" xmlns="http://www.w3.org/2000/svg">` +
		`<path d="M15 0C6.72 0 0 6.72 0 15 0 26.08 15 41 15 41S30 26.08 30 15C30 6.72 23.28 0 15 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>` +
		`</svg>` +
		`<span class="marker-pin-icon"></span>` +
		`<span class="marker-pin-label" aria-hidden="true">${label}</span>`;

	return el;
}

// Page chrome that overlays the map and can hide a focused marker. The menu is
// a full-width top sheet on mobile and a left-docked panel on desktop; the site
// header is a full-width band at the top when shown.
const MAP_UI_SELECTORS = ['.map-menu', '#branding'];

/**
 * Measure how far page chrome intrudes over each edge of the map container.
 *
 * Each overlapping element is "docked" to an edge: full-width elements to the
 * top/bottom, narrower panels to the left/right. Returns the map container rect
 * plus the largest pixel inset found on each side.
 *
 * @returns {{ map: DOMRect, inset: {top: number, right: number, bottom: number, left: number} }}
 */
function chromeInsets() {
	const map = W.map.getContainer().getBoundingClientRect();
	const inset = { top: 0, right: 0, bottom: 0, left: 0 };

	MAP_UI_SELECTORS.forEach((selector) => {
		const el = document.querySelector(selector);
		if (!el) return;

		const r = el.getBoundingClientRect();
		const overlapW = Math.min(r.right, map.right) - Math.max(r.left, map.left);
		const overlapH = Math.min(r.bottom, map.bottom) - Math.max(r.top, map.top);
		if (overlapW <= 0 || overlapH <= 0) return; // hidden or not over the map

		if (overlapW >= 0.7 * map.width) {
			// Full-width band: docks to the top or bottom edge.
			if ((r.top + r.bottom) / 2 < map.top + map.height / 2) {
				inset.top = Math.max(inset.top, r.bottom - map.top);
			} else {
				inset.bottom = Math.max(inset.bottom, map.bottom - r.top);
			}
		} else {
			// Narrower panel: docks to the left or right edge.
			if ((r.left + r.right) / 2 < map.left + map.width / 2) {
				inset.left = Math.max(inset.left, r.right - map.left);
			} else {
				inset.right = Math.max(inset.right, map.right - r.left);
			}
		}
	});

	return { map, inset };
}

/**
 * Pan the map to reveal a marker when it receives keyboard focus.
 *
 * Don't pan for pointer focus (so clicking pin doesn't trigger a surprise pan),
 * for markers already visible within the unobstructed area, and honors
 * reduced-motion. A marker that is off-screen *or* hidden behind the menu panel
 * or header is eased into the clear region, satisfying WCAG 2.2 SC 2.4.11
 * (Focus Not Obscured) and 2.4.7 (Focus Visible).
 *
 * @param {HTMLElement} markerEl - The marker button element.
 * @param {[number, number]} lngLat - The marker's [lng, lat] coordinates.
 */
export function panIntoViewOnFocus(markerEl, lngLat) {
	markerEl.addEventListener('focus', () => {
		// Only react to keyboard focus, not pointer clicks.
		if (!markerEl.matches(':focus-visible')) return;

		// Focusing an off-screen marker makes the browser scroll the map
		// container (it is overflow:hidden but still programmatically
		// scrollable) to reveal it, shifting the canvas. Undo that — we move
		// the camera instead.
		const container = W.map.getContainer();
		container.scrollLeft = 0;
		container.scrollTop = 0;

		const { map, inset } = chromeInsets();
		const pad = 16; // comfort margin from the edges and chrome

		// Project the marker to container pixels. Unlike getBoundingClientRect,
		// this reflects the map's geographic transform and is unaffected by any
		// stray DOM scroll, so off-screen markers report off-screen positions.
		const pos = W.map.project(lngLat);

		// Already within the unobstructed area? Nothing to do.
		const visible =
			pos.x >= inset.left + pad &&
			pos.x <= map.width - inset.right - pad &&
			pos.y >= inset.top + pad &&
			pos.y <= map.height - inset.bottom - pad;
		if (visible) return;

		// Center the marker within the unobstructed area (offset is not persisted).
		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		W.map.easeTo({
			center: lngLat,
			offset: [(inset.left - inset.right) / 2, (inset.top - inset.bottom) / 2],
			duration: reduceMotion ? 0 : 400,
		});
	});
}
